import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';

import { AuthService } from './auth.service';
import { catchError, switchMap } from 'rxjs/operators';
import { B2CPolicies } from '../models/auth-config.model';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { UtilsService } from 'app/back-office/shared/services/core/utils.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private msalService: MsalService,
    private authService: AuthService,
    private _utils: UtilsService,
    private _router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const props = Object.keys(environment.urls);
    if (props.some(p => !!environment.urls[p].authRequired && !!request?.url?.includes(environment.urls[p].url))) {
      const token: string = this.authService.tokenValue;
      token ? request = this.addTokenHeader(request, token) : console.error('No token');
    }

    if (request.headers.has('skipErrorHandling')) {
      const newHeaders = request.headers.delete('skipErrorHandling');
      const cleanReq = request.clone({ headers: newHeaders });
      return next.handle(cleanReq);
    }
    
    return next.handle(request).pipe(
      catchError(error => {
        return this.handleHttpError(request, next, error as HttpErrorResponse, request.method);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handleHttpError(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse, method?: string): Observable<HttpEvent<any>> {
    switch (error.status) {
      case 401:
        return this.handle401Error(request, next);
      case 400:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Request not valid'), error?.error?.error?.details, method, error.url);
        throw error;
      case 403:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Forbidden'), error?.error?.error?.details, method, error.url);
        throw error;
      case 404:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Not Found'), error?.error?.error?.details, method, error.url);
        throw error;
      case 405:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Method Not Allowed'), error?.error?.error?.details, method, error.url);
        throw error;
      case 500:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Internal Server Error'), error?.error?.error?.details, method, error.url);
        throw error;
      case 501:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Not Implemented'), error?.error?.error?.details, method, error.url);
        throw error;
      case 503:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'Service Unavailable'), error?.error?.error?.details, method, error.url);
        throw error;
      default:
        this.openErrorToast(error.status, (error?.error?.error?.message || 'An error occurred'), error?.error?.error?.details, method, error.url);
        throw error;
    }
  }

  private openErrorToast(status: number, message: string, detail: string, method?: string, url?: string) {

    const messageForSnack = `
      ${method ? `<strong>Method:</strong> ${method.toUpperCase()}` : ''}
      ${url ? `<br><strong>Url:</strong> ${url}` : ''}
      <br><strong>Status:</strong> ${status}
      <br><strong>Error message:</strong> ${message}
      ${detail ? `<br><pre>${detail}</pre>` : ''}
      `;

    this._utils.showSnackbar(messageForSnack, null, 'danger', null, true);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let allAccounts = this.msalService.instance.getAllAccounts();
    const account = allAccounts[0];
    return this.msalService.acquireTokenSilent({
      account: account,
      scopes: [B2CPolicies().scope],
      redirectUri: window.location.href
    }).pipe(
      catchError(() => {
        this.authService.logout();
        return of(null);
      }),
      switchMap((token: any) => {
        if (token.accessToken) {
          this.authService.setToken(token.accessToken);
          return next.handle(this.addTokenHeader(request, token.accessToken)).pipe(
            catchError(() => {
              this.authService.logout();
              return of(null);
            })
          );
        }
        else this._router.navigateByUrl('errors/unauthorized');
      })
    );
  }
}
